import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Wrench, Truck, FileText } from 'lucide-react';

const services = [
  {
    id: 'autopartes',
    icon: Wrench,
    title: 'Autopartes',
    subtitle: 'Refacciones',
    description: 'Contamos con una gran variedad de refacciones y autopartes usadas',
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80'
  },
  {
    id: 'traslados',
    icon: Truck,
    title: 'Traslados y Servicios de Grúa',
    subtitle: 'Grúa, caja cerrada, madrinas',
    description: 'Ofrecemos el servicio de traslado de su unidad a cualquier parte de la República, al mejor precio y con la mejor calidad.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'
  },
  {
    id: 'gestoria',
    icon: FileText,
    title: 'Trámites y Gestoría',
    subtitle: 'Trámites',
    description: 'Ofrecemos el servicio de gestoría para la mayoría de los trámites que requiera de su vehículo',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80'
  }
];

export default function ServiciosPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-card">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary text-sm font-bold uppercase tracking-widest mb-4">
              Lo que ofrecemos
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-foreground mb-6">
              Nuestros Servicios
            </h1>
            <p className="text-lg text-muted-foreground">
              Además de la venta de vehículos, ofrecemos servicios complementarios para satisfacer todas tus necesidades automotrices.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className="bg-card border border-border rounded-sm overflow-hidden group hover:border-primary/50 transition-colors"
                data-testid={`service-card-${service.id}`}
              >
                {/* Image */}
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Content */}
                <div className="p-6 md:p-8">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  
                  <p className="text-primary text-sm font-bold uppercase tracking-widest mb-2">
                    {service.subtitle}
                  </p>
                  
                  <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4">
                    {service.title}
                  </h2>
                  
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground mb-6">
              ¿Necesitas alguno de nuestros servicios?
            </h2>
            <p className="text-muted-foreground mb-8">
              Contáctanos para más información sobre nuestros servicios de autopartes, traslados y gestoría.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://api.whatsapp.com/send?phone=523325483444&text=Hola,%20me%20interesa%20información%20sobre%20sus%20servicios"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-btn justify-center"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contactar por WhatsApp
              </a>
              <a 
                href="tel:+523325483444"
                className="btn-outline inline-flex items-center justify-center"
              >
                Llamar ahora
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
